import React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckInIcon,
  PlayArrow as StartIcon,
  Done as FinishIcon,
  Cancel as CancelIcon,
  Pets as PetIcon
} from '@mui/icons-material';
import type { Appointment } from '@app-types/reception';
import { VisitStatusBadge } from './VisitStatusBadge';
import { formatTimeOnly } from '@utils/dateFormatter';

interface AppointmentRowProps {
  appointment: Appointment;
  onCheckin: (id: string) => void;
  onStartConsultation: (id: string) => void;
  onFinish: (id: string) => void;
  onCancel: (id: string) => void;
}

export const AppointmentRow: React.FC<AppointmentRowProps> = ({
  appointment,
  onCheckin,
  onStartConsultation,
  onFinish,
  onCancel
}) => {
  const ownerName = appointment.owner 
    ? `${appointment.owner.firstName} ${appointment.owner.lastName}`
    : 'N/A';

  const doctorName = appointment.doctor
    ? `Dr. ${appointment.doctor.firstName} ${appointment.doctor.lastName}`
    : 'N/A';

  const scheduledTime = formatTimeOnly(appointment.scheduledAt);
  const canCheckin = appointment.status === 'scheduled';
  const canStart = appointment.status === 'arrived';
  const canFinish = appointment.status === 'in_consultation';
  const canCancel = ['scheduled', 'arrived'].includes(appointment.status);

  return (
    <TableRow hover>
      {/* Ora */}
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {scheduledTime}
        </Typography>
      </TableCell>

      {/* Animal */}
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <PetIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {appointment.animal?.name || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {appointment.animal?.species || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Proprietar */}
      <TableCell>
        <Typography variant="body2">{ownerName}</Typography>
        {appointment.owner?.phone && (
          <Typography variant="caption" color="text.secondary">
            {appointment.owner.phone}
          </Typography>
        )}
      </TableCell>

      {/* Medic */}
      <TableCell>
        <Typography variant="body2">{doctorName}</Typography>
        {appointment.doctor?.specialty && (
          <Typography variant="caption" color="text.secondary">
            {appointment.doctor.specialty}
          </Typography>
        )}
      </TableCell>

      {/* Tip serviciu */}
      <TableCell>
        <Typography variant="body2">{appointment.serviceType}</Typography>
      </TableCell>

      {/* Status */}
      <TableCell>
        <VisitStatusBadge status={appointment.status} />
      </TableCell>

      {/* Actiuni */}
      <TableCell>
        <Box display="flex" gap={0.5}>
          {canCheckin && (
            <Tooltip title="Check-in (Pacient sosit)">
              <IconButton 
                size="small" 
                color="success"
                onClick={() => onCheckin(appointment.id)}
              >
                <CheckInIcon />
              </IconButton>
            </Tooltip>
          )}

          {canStart && (
            <Tooltip title="Start consultatie">
              <IconButton 
                size="small" 
                color="warning"
                onClick={() => onStartConsultation(appointment.id)}
              >
                <StartIcon />
              </IconButton>
            </Tooltip>
          )}

          {canFinish && (
            <Tooltip title="Finalizeaza vizita">
              <IconButton 
                size="small" 
                color="success"
                onClick={() => onFinish(appointment.id)}
              >
                <FinishIcon />
              </IconButton>
            </Tooltip>
          )}

          {canCancel && (
            <Tooltip title="Anuleaza programare">
              <IconButton 
                size="small" 
                color="error"
                onClick={() => onCancel(appointment.id)}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

